// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import {
    AutocompleteItemModel,
    BrandModel,
} from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Button, VisuallyHidden } from "@insite/mobius/index";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngSignInButton, { XngSignInButtonStyles } from "@stanion/App/Components/Common/XngSignInButton";
import { miniCartLineStyles } from "@stanion/App/Components/MiniCart/styles";
import ProductPreviousPurchase from "@stanion/App/Components/Product/ProductPreviousPurchase";
import ProductQuantityOrdered, {
    ProductQuantityOrderedStyles,
} from "@stanion/Overrides/Components/ProductQuantityOrdered";
import { color, ellipsis, isEmptyString, mergeWithCss, xScreen } from "@stanion/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Chnages
    query?: string;
    doSearch?: () => void;
    products: ProductContextModel[];
    isAuthenticated?: boolean;
}

type Props = OwnProps;

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    shopAllButton?: ButtonPresentationProps;
    productItemInfo?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityOrderedStyles?: ProductQuantityOrderedStyles;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    partNumbers?: {
        wrapper?: InjectableCss;
        lable?: TypographyProps;
        value?: TypographyProps;
    };
    productWrapper?: InjectableCss;
    productContainer?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    actionsContainer?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productPreviousPurchase?: TypographyPresentationProps;
    unitOfMeasureText?: TypographyPresentationProps;
    price?: ProductPriceStyles;
    signInButton?: XngSignInButtonStyles;
    tabletActionsWrapper?: InjectableCss;
    mobileAndDesktopActionsGridItem?: GridItemProps;
    previousPurchaseWrapper?: InjectableCss;
    previousPurchaseGridItem?: GridItemProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: 16,
        weight: "normal",
        color: "text.main",
        ellipsis: true,
        css: css`
            letter-spacing: 0;
            line-height: 17px;
            margin-right: 12px;
            & > ${TypographyStyle} {
                display: block;
                font-size: 22px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 28px;
            }
        `,
    },
    container: {
        gap: 0,
        css: css`
            margin: 12px 0px;
            cursor: pointer;
            color: ${color("text.main")};
            padding-bottom: 10px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    imageGridItem: {
        width: [3, 3, 2, 2, 2],
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "xs")} {
                width: 92px;
                min-width: 92px;
                max-width: 92px;
            }
        `,
    },
    infoGridItem: {
        width: [9, 9, 10, 5, 5],
        css: css`
            height: auto;
            margin-top: 4px;
            flex-direction: column;
            padding: 0 15px;
            ${xScreen("lte", "xs")} {
                width: auto;
                max-width: calc(100% - 92px);
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-wrap: wrap;
            padding-bottom: 8px;
            margin-bottom: 10px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    shopAllButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            ${xScreen("lte", "sm")} {
                margin-top: 10px;
            }
        `,
    },
    productItemInfo: {
        css: css`
            flex-direction: column;
            width: 100%;
        `,
    },
    partNumbers: {
        ...miniCartLineStyles.partNumbers,
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
                margin-top: 13px;
                margin-bottom: 5px;
                ${xScreen("lte", "xs")} {
                    margin-top: 0;
                    margin-bottom: 10px;
                    height: 100%;
                    align-content: flex-end;
                    flex-wrap: wrap;
                }
                span {
                    margin-bottom: 0;
                }
            `,
        },
        lable: {
            size: 12,
            weight: "bold",
            color: "text.main",
            css: css`
                flex-shrink: 0;
                letter-spacing: 0;
                line-height: 13px;
                margin-right: 5px;
                ${ellipsis(1)};
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            width: 100%;
            ${xScreen("lte", "xs")} {
                margin-bottom: 10px;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                color: "text.main",
                size: 13,
                lineHeight: "18px",
                css: css`
                    width: 100%;
                    ${ellipsis(3)};
                    ${wrapInContainerStyles}
                `,
            },
            css: css`
                height: auto;
                margin-bottom: 0;
            `,
        },
    },
    productQuantityOrderedStyles: {
        wrapper: {
            css: css`
                display: flex;
                border: 1px solid ${color("common.ui5")};
                padding: 0 9px;
                min-width: 92px;
                max-width: 92px;
                width: 92px;
                margin-right: 24px;
                margin-left: auto;
                ${xScreen("lte", "xs")} {
                    margin-right: 15px;
                }
            `,
        },
    },
    productImage: {
        img: {
            css: css`
                width: 92px;
                height: 92px;
            `,
        },
    },
    productAddToCartButtonStyles: {
        css: css`
            min-height: 30px;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    productPriceWrapper: {
        css: css`
            margin-left: auto;
            margin-bottom: 8px;
            display: flex;
            ${xScreen("gte", "sm")} {
                justify-content: flex-end;
            }
            ${xScreen("lte", "xs")} {
                margin-left: 105px;
                margin-bottom: 15px;
            }
        `,
    },
    productBrandStyles: {
        nameText: {
            size: 11,
            lineHeight: "15px",
            css: css`
                margin-bottom: 2px;
            `,
        },
        nameLink: {
            css: css`
                width: 100%;
                margin-bottom: 2px;
            `,
            typographyProps: {
                size: 11,
                lineHeight: "15px",
            },
        },
    },
    price: {
        wrapper: {
            css: css`
                width: auto;
                ${xScreen("gte", "sm")} {
                    display: flex;
                    justify-content: flex-end;
                }
            `,
        },
        price: {
            priceText: {
                size: 18,
                lineHeight: "26px",
            },
        },
    },
    signInButton: {
        container: {
            css: css`
                padding: 15px;
                ${xScreen("lte", "xs")} {
                    margin-bottom: 10px;
                }
            `,
        },
        containerSecondLevel: {
            css: css`
                line-height: 14px;
                font-size: 12px;
            `,
        },
        textWrapper: {
            size: 12,
            lineHeight: "14px",
        },
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
            `,
        },
    },
    unitOfMeasureText: {
        weight: "bold",
        color: "common.ui4",
        size: 12,
        transform: "uppercase",
        css: css`
            display: block;
            flex-shrink: 0;
        `,
    },
    tabletActionsWrapper: {
        css: css`
            width: 100%;
            ${xScreen("lte", "xs")} {
                display: none;
            }
            ${xScreen("gte", "lg")} {
                display: none;
            }
        `,
    },
    mobileAndDesktopActionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            display: flex;
            align-items: flex-end;
            ${xScreen("lte", "md")} {
                display: none;
            }
            ${xScreen("lte", "xs")} {
                display: block;
            }
        `,
    },
    previousPurchaseWrapper: {
        css: css`
            ${xScreen("gte", "lg")} {
                display: none;
            }
        `,
    },
    previousPurchaseGridItem: {
        width: 12,
        css: css`
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, isAuthenticated } = this.props;
        const styles = this.styles;

        const actions = (productContext: ProductContextModel) => (
            <StyledWrapper
                {...styles.productItemInfo}
                className="autocomplete-products-price-grid-item"
                {...{ isAuthenticated }}
            >
                {this.props.isAuthenticated ? (
                    <>
                        <StyledWrapper {...styles.productPriceWrapper}>
                            <VisuallyHidden
                                as="label"
                                htmlFor={`${productContext.product.id}-uom`}
                                id={`${productContext.product.id}-uom-label`}
                            >
                                {translate("U/M")}
                            </VisuallyHidden>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.price}
                            />
                            {productContext.product.properties.unitOfMeasure && (
                                <Typography {...styles.unitOfMeasureText}>
                                    / {productContext.product.properties.unitOfMeasure}
                                </Typography>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.addToCartWrapper}>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${productContext.product.id}-qty`}
                            />
                            <ProductAddToCartButton
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </StyledWrapper>
                    </>
                ) : (
                    <XngSignInButton extendedStyles={styles.signInButton} />
                )}
            </StyledWrapper>
        )

        // XNG-Changes Structure
        return (
            <>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Recommended Products for")}
                        <Typography ellipsis={true} data-test-selector="autocompleteProducts_Header">
                            {this.props.query}
                        </Typography>
                    </Typography>
                    <Button
                        {...styles.shopAllButton}
                        onClick={this.props.doSearch}
                        data-test-selector="search-view-all"
                    >
                        {translate("Shop All")}
                    </Button>
                </StyledWrapper>
                {products.map(productContextx => {
                    const productContext = {
                        ...productContextx,
                    };
                    return (
                        <ProductContext.Provider key={productContext.product.id} value={productContext}>
                            <GridContainer {...styles.container} className="autocomplete-products-container">
                                <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                                    <ProductImage
                                        product={productContext}
                                        extendedStyles={mergeWithCss(styles.productImage, {
                                            linkWrappingImage: {
                                                onClick: () => hideAll(),
                                            },
                                        })}
                                    />
                                    <StyledWrapper {...styles.previousPurchaseWrapper}>
                                        <ProductPreviousPurchase
                                            product={productContext.product}
                                            extendedStyles={styles.productPreviousPurchase}
                                        />
                                    </StyledWrapper>
                                </GridItem>

                                <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                    <ProductBrand
                                        brand={productContext.product?.brand as BrandModel}
                                        showLogo={false}
                                        extendedStyles={mergeWithCss(styles.productBrandStyles, {
                                            nameLink: {
                                                onClick: () => hideAll(),
                                            },
                                        })}
                                    />
                                    <ProductDescription
                                        product={productContext}
                                        extendedStyles={mergeWithCss(styles.productDescriptionStyles, {
                                            productDetailLink: {
                                                onClick: () => hideAll(),
                                            },
                                        })}
                                    />
                                    <StyledWrapper {...styles.partNumbers?.wrapper}>
                                        {productContext.product?.customerProductNumber &&
                                            !isEmptyString(productContext.product?.customerProductNumber) ? (
                                            <>
                                                <Typography as="b" {...styles.partNumbers?.lable}>
                                                    {translate("Your Part #")}:
                                                </Typography>
                                                <Typography
                                                    data-test-selector="customerProductNumber"
                                                    {...styles.partNumbers?.value}
                                                >
                                                    {productContext.product?.customerProductNumber}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <Typography as="b" {...styles.partNumbers?.lable}>
                                                    {translate("Stanion #")}:
                                                </Typography>
                                                <Typography
                                                    data-test-selector="erpNumber"
                                                    {...styles.partNumbers?.value}
                                                >
                                                    {productContext.product?.productNumber}
                                                </Typography>
                                            </>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.tabletActionsWrapper}>
                                        {actions(productContext)}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.mobileAndDesktopActionsGridItem}>
                                    {actions(productContext)}
                                </GridItem>
                                <GridItem {...styles.previousPurchaseGridItem}>
                                    <ProductPreviousPurchase
                                        product={productContext.product}
                                        extendedStyles={styles.productPreviousPurchase}
                                    />
                                </GridItem>
                            </GridContainer>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default AutocompleteProducts;


